
import {defineComponent} from "vue";
import {Exception} from "@/api/interfaces";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import {CatalogProductsRestService} from "@/services/rest/cpc-mgmt/CatalogProductsRestService";
import {ProductStockInfoRestDto} from "@/api/pharma-cpc-product-mgmt/models";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import BaseCardBody from "@/components/UI/Bootstrap/Card/BaseCardBody.vue";
import BaseCardHeader from "@/components/UI/Bootstrap/Card/BaseCardHeader.vue";
import BaseCard from "@/components/UI/Bootstrap/Card/BaseCard.vue";
import {AbilityContext} from "@/context/AbilityContext";
import ProductDetailMinimalStock from "@/components/layouts/catalog/product-detail/ProductDetailMinimalStock.vue";
import ProductDetailMaximumOrderQuantity
  from "@/components/layouts/catalog/product-detail/ProductDetailMaximumOrderQuantity.vue";
import {AuthContext} from "@/context/AuthContext";
import BackInStockNotifications
  from "@/components/layouts/catalog/product-detail/ProductDetailBackInStockNotifications.vue";

export default defineComponent({
  name: "ProductDetailStockTab",

  components: {
    BackInStockNotifications,
    ProductDetailMaximumOrderQuantity,
    ProductDetailMinimalStock, BaseCard, BaseCardHeader, BaseCardBody, AlertError2},
  emits: ["savedSuccessfully"],
  props: {
    productCode: {
      type: String,
      required: true
    },
    cnkCode: {
      type: String,
      required: false
    }
  },
  data: () => ({
    exceptions: [] as Exception[],
    tabUI: UIStateDto.createWithDefaults(),
    stockInfo: null as ProductStockInfoRestDto | null,
  }),
  mounted() {
    this.reloadContent();
  },
  computed: {
    AbilityContext() {
      return AbilityContext
    },
    AuthContext() {
      return AuthContext
    },
    widgetColumnClass(): string {
      let nrOfColumns: number = this.nrOfRegularColumns;
      // TODO: remove isDpUser
      if (nrOfColumns === 1 && (AbilityContext.isAuthorizedForFeature('PRODUCT_MINIMAL_STOCK') || AbilityContext.isAuthorizedForFeature('PRODUCT_MAXIMAL_ORDER_AMOUNT')) && AuthContext.isDpUser()) {
        nrOfColumns++;
      }
      const colSpan: number = 12 / nrOfColumns;

      return `col-${colSpan}`;
    },
    renderMinStockAndMaxOrderQuantityInSeparateColumn(): boolean {
      return this.nrOfRegularColumns < 2
    },
    nrOfRegularColumns(): number {
      let nrOfColumns: number = 0;
      if (this.stockInfo?.catalog_stock_info) {
        nrOfColumns++;
      }
      if (this.stockInfo?.customers?.length) {
        nrOfColumns++;
      }
      if (this.stockInfo?.wholesalers?.length) {
        nrOfColumns++;
      }

      return nrOfColumns;
    },
    firstRenderedColumn(): number {
      if (this.stockInfo?.catalog_stock_info) {
        return 0;
      }
      if (this.stockInfo?.customers?.length) {
        return 1;
      }
      if (this.stockInfo?.wholesalers?.length) {
        return 2;
      }
      return 0;
    },
  },
  methods: {
    async reloadContent() {
      this.tabUI
          .setNotReady()
          .clearError();

      try {
        if (AbilityContext.isAuthorizedForFeature('ADVANCED_MGMT_CUSTOMER_CATALOG_PRODUCTS')) {
          if (this.productCode) {
            const service = CatalogProductsRestService.getInstance();
            this.stockInfo = await service.findProductStockInfo(this.productCode);
          }
        }
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.tabUI.setReady();
      }
    },
    formatDate(dateString: string): string {
      if (!dateString) return '-';

      try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(this.$i18n.locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(date);
      } catch (e) {
        return dateString;
      }
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
          .create()
          .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.tabUI as UIStateDto))
          .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.tabUI as UIStateDto))
          .catch(exceptions, true);
    }
  },
});
